import { Button } from '@app/components/various/Button';
import { Download } from '@app/components/various/Icons';
import { Skeleton } from '@app/components/various/Skeleton';
import { BREAKPOINTS } from '@app/constants/breakpoints';
import { getUser } from '@app/ducks';
import { getIsInvoiceFull } from '@app/logic/invoices';
import { getIsBuyer } from '@app/logic/User';
import { paths } from '@app/paths';
import { useInvoiceDownload } from '@app/services/hooks/invoice/useInvoiceDownload';
import { useIsScreenSize } from '@app/utils/hooks';
import { isDefined } from '@app/utils/is';
import { Invoice } from '@typings';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

interface Props {
  invoice: Invoice.Full | Invoice.Base | undefined;
  invoiceIdentifiers: Invoice.InvoiceIdentifiers | null;
  isLoading: boolean;
}

export const InvoiceHeader = ({ invoice, invoiceIdentifiers, isLoading }: Props) => {
  const { t } = useTranslation(['invoices', 'common']);
  const { downloadInvoice } = useInvoiceDownload();
  const user = useSelector(getUser);
  const isBuyer = getIsBuyer(user);
  const isMobile = useIsScreenSize(BREAKPOINTS.SM);
  const canDownloadInvoice = isDefined(invoiceIdentifiers) && isDefined(invoice);

  const invoiceHeading =
    isLoading ?
      <Skeleton type="text" itemClassName="w-24" size="xlarge" />
    : <h2 className="heading-2 sm:heading-1" data-testid="invoice-heading">
        {t('invoices:invoice_number', {
          invoiceNumber: invoice?.invoiceNumber,
        })}
      </h2>;

  if (isDefined(invoice) && !getIsInvoiceFull(invoice)) {
    return invoiceHeading;
  }

  const onInvoiceDownload = () => {
    if (!canDownloadInvoice) {
      return;
    }

    downloadInvoice({ ...invoiceIdentifiers, invoiceNumber: invoice.invoiceNumber });
  };

  return (
    <div className="flex items-center justify-between">
      {invoiceHeading}
      <div className="flex gap-2">
        {isBuyer && (
          <Button to={paths.INVOICES} as={Link} variant="bordered" color="secondary" size="large">
            {isMobile ? t('common:back') : t('invoices:back_to_invoices')}
          </Button>
        )}
        <Button
          aria-label={t('invoices:download_pdf')}
          variant="bordered"
          color="secondary"
          icon={Download}
          onClick={onInvoiceDownload}
          size="large"
          disabled={!canDownloadInvoice}
        >
          {!isMobile && t('invoices:download_pdf')}
        </Button>
      </div>
    </div>
  );
};
